import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import { MAIN_ROUTES } from '../constants/routes';

const CookiePolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const [cookieConsent, setCookieConsent] = useState<{
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
  }>(() => {
    // Initialize from localStorage if available
    const savedConsent = localStorage.getItem('cookieConsent');
    return savedConsent ? JSON.parse(savedConsent) : {
      essential: true, // Essential cookies are always enabled
      analytics: false,
      marketing: false
    };
  });

  useEffect(() => {
    // Save consent preferences to localStorage whenever they change
    localStorage.setItem('cookieConsent', JSON.stringify(cookieConsent));
  }, [cookieConsent]);

  const handleAcceptAll = () => {
    setCookieConsent({
      essential: true,
      analytics: true,
      marketing: true
    });
    navigate(MAIN_ROUTES.SIGNUP.path);
  };

  const handleRejectNonEssential = () => {
    setCookieConsent({
      essential: true,
      analytics: false,
      marketing: false
    });
    navigate(MAIN_ROUTES.SIGNUP.path);
  };

  const handleCookieSettings = () => {
    // You can implement a modal or expandable section for detailed cookie settings
    // For now, we'll just toggle analytics and marketing cookies
    setCookieConsent(prev => ({
      ...prev,
      analytics: !prev.analytics,
      marketing: !prev.marketing
    }));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">
                Cookie Policy
        </h1>
        
        <div className="space-y-8">
          <section>
            <p className="text-gray-300 mb-4">
              <strong>Effective Date:</strong> May 7, 2025
            </p>
            <p className="text-gray-300 mb-4">
              This Cookie Policy explains how <strong>Valuefy Technologies Private Limited</strong> ("Valuefy", "we", "us", or "our") uses cookies and similar technologies on our website and services (collectively referred to as the "Services").
            </p>
            <p className="text-gray-300">
              By using our Services, you agree to the use of cookies as described in this policy. You can manage or withdraw your consent at any time through your browser settings or via our cookie consent tool.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">1. What Are Cookies?</h2>
            <p className="text-gray-300">
              Cookies are small text files stored on your device when you visit a website. They help us recognize your browser, remember your preferences, and enhance your user experience. We may also use similar tracking technologies such as web beacons, pixels, and local storage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">2. Types of Cookies We Use</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li><strong>Essential Cookies:</strong> Necessary for the proper functioning of our Services and cannot be disabled in our systems.</li>
              <li><strong>Performance and Analytics Cookies:</strong> Help us understand how users interact with our website. We may use tools like Google Analytics.</li>
              <li><strong>Functional Cookies:</strong> Enable enhanced functionality and personalization such as remembering preferences.</li>
              <li><strong>Targeting and Advertising Cookies:</strong> Used by us or third-party partners to build a profile of your interests and show relevant ads.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">3. Managing Your Preferences</h2>
            <p className="text-gray-300 mb-4">
              You can manage or disable cookies through your browser settings. Disabling some cookies may affect the functionality of our Services.
            </p>
            <p className="text-gray-300 mb-4">You can also opt-out of certain third-party cookies using these links:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-300">
              <li><a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">Google Ads Settings</a></li>
              <li><a href="https://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">YourOnlineChoices (EU)</a></li>
              <li><a href="https://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">Network Advertising Initiative (US)</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">4. Updates to This Policy</h2>
            <p className="text-gray-300">
              We may update this Cookie Policy periodically to reflect changes in technology, legal requirements, or our practices. Any updates will be posted on this page with a revised effective date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 bg-gradient-to-r from-[#F8FAFC] to-[#94A3B8] bg-clip-text text-transparent">5. Contact Us</h2>
            <p className="text-gray-300">
              If you have any questions about our use of cookies, you can contact us at:
            </p>
            <p className="text-gray-300 mt-2">
              <strong>Email:</strong> privacy@valuefy.com<br />
              <strong>Phone:</strong> +1 (555) 123-4567
            </p>
          </section>
        </div>

        <div className="mt-12 p-6 bg-[rgba(69,42,124,0.10)] rounded-lg">
          <p className="text-gray-300 mb-4">
            <strong>We use cookies</strong> to improve your browsing experience, provide personalized content, and analyze our traffic. By clicking "Accept All," you consent to our use of cookies. You can manage your preferences or withdraw your consent at any time.
          </p>
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={handleAcceptAll}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-md transition-colors"
            >
              Accept All
            </button>
            <button 
              onClick={handleRejectNonEssential}
              className="bg-transparent border border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-white px-6 py-2 rounded-md transition-colors"
            >
              Reject Non-Essential
            </button>
          </div>
          <div className="mt-4 text-sm text-gray-400">
            Current settings: Essential ({cookieConsent.essential ? 'Enabled' : 'Disabled'}), 
            Analytics ({cookieConsent.analytics ? 'Enabled' : 'Disabled'}), 
            Marketing ({cookieConsent.marketing ? 'Enabled' : 'Disabled'})
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage; 